import { ChangeEvent, memo, useEffect, useRef, useState } from 'react';
import { FilePlus2, Image as ImageIcon, Loader2, Mic, Sparkles, Square, Trash2 } from 'lucide-react';
import { getAccessToken } from '../lib/api';
import { FormField } from '../lib/forms';
import { MagicDiffModal, type MagicResult } from './MagicDiffModal';
import { Hint } from './Hint';

/** Sprint 19 · server-persisted attachment (LeadDetail) — id known. */
export interface MagicServerAttachment {
  id: string;
  filename: string;
  mime: string;
  isImage: boolean;
}

/** Sprint 19 · local attachment (Capture, vor lead-create) — Datei lokal. */
export interface MagicLocalAttachment {
  file: File;
  preview: string | null;
  isImage: boolean;
}

interface Props {
  field: FormField;
  value: unknown;
  onChange: (v: string) => void;
  /** Sprint 18 (S18.4) · Sticky-bottom signalling, optional. */
  onMagicAccepted?: () => void;
  /** Sprint 19 (S19.2/S19.3) · Anhänge unter Notiz. */
  localAttachments?: MagicLocalAttachment[];
  serverAttachments?: MagicServerAttachment[];
  onAddLocalAttachment?: (file: File) => void;
  onRemoveLocalAttachment?: (idx: number) => void;
  /** Sprint 19 (S19.4) · Re-Magic-Hint im LeadDetail. */
  showAttachmentControls?: boolean;
}

const ATTACH_MIMES = 'image/jpeg,image/png,image/webp,application/pdf';

function VoiceMagicTextareaInner({
  field,
  value,
  onChange,
  onMagicAccepted,
  localAttachments,
  serverAttachments,
  onAddLocalAttachment,
  onRemoveLocalAttachment,
  showAttachmentControls,
}: Props) {
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [magic, setMagic] = useState<MagicResult | null>(null);
  const [magicRawText, setMagicRawText] = useState('');
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const attachInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(
    () => () => {
      mediaRecorderRef.current?.stream.getTracks().forEach((t) => t.stop());
    },
    [],
  );

  const text = typeof value === 'string' ? value : '';
  const inputId = `f-${field.id}`;

  // Sprint 19 · Anhänge-Counts für Magic-Confirm
  const localImageCount = (localAttachments ?? []).filter((a) => a.isImage).length;
  const serverImageAtts = (serverAttachments ?? []).filter((a) => a.isImage);
  const serverImageCount = serverImageAtts.length;
  const hasAnyImages = localImageCount > 0 || serverImageCount > 0;

  async function startRecording() {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];
      mr.ondataavailable = (ev) => ev.data.size && chunksRef.current.push(ev.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        await uploadAudio(blob);
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch (err) {
      setError(`Mikrofon nicht verfügbar: ${(err as Error).message}`);
    }
  }

  function stopRecording() {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  }

  async function uploadAudio(blob: Blob) {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', blob, 'audio.webm');
      fd.append('field_id', field.id);
      const resp = await fetch('/api/voice/transcribe', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: fd,
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = (await resp.json()) as { text: string };
      onChange(text ? `${text}\n${data.text}` : data.text);
    } catch (err) {
      setError(`Voice fehlgeschlagen: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  function handleAttachmentInput(e: ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    if (file && onAddLocalAttachment) onAddLocalAttachment(file);
    if (attachInputRef.current) attachInputRef.current.value = '';
  }

  // Sprint 41 · State für Magic-Confirm-Modal (Sprint 43: lokale Bilder JETZT)
  // - 'closed': Modal nicht offen
  // - 'choose-vision-server': Server-Bilder vorhanden — User wählt Vision/Text-Only
  // - 'choose-vision-local': lokale Bilder vorhanden — User wählt Vision-Multipart/Text-Only
  type MagicAskMode = 'closed' | 'choose-vision-server' | 'choose-vision-local';
  const [magicAsk, setMagicAsk] = useState<MagicAskMode>('closed');

  async function runMagic() {
    if (!text.trim()) {
      setError('Erst Notiz eintippen oder diktieren.');
      return;
    }
    setError(null);

    // Server-Bilder (saved Lead) → Modal mit Vision/Text-Only-Option
    if (serverImageCount > 0) {
      setMagicAsk('choose-vision-server');
      return;
    }
    // Sprint 43 · Lokale Bilder (vor Save) → Modal mit Vision-Multipart-Option
    if (localImageCount > 0) {
      setMagicAsk('choose-vision-local');
      return;
    }

    // Keine Bilder → direkt text-only ausführen (JSON-Pfad)
    await executeMagicJson([]);
  }

  async function executeMagicJson(attachmentIdsForVision: string[]): Promise<void> {
    setMagicAsk('closed');
    setBusy(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        raw_text: text,
        target_field: field.id,
      };
      if (attachmentIdsForVision.length > 0) {
        body.attachment_ids = attachmentIdsForVision;
      }
      const resp = await fetch('/api/text/magic', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Authorization: `Bearer ${getAccessToken() ?? ''}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const detail = await resp.json().catch(() => ({}));
        throw new Error(
          (detail as { detail?: string }).detail ?? `HTTP ${resp.status}`,
        );
      }
      const data = (await resp.json()) as { structured: MagicResult; vision_used?: boolean };
      setMagicRawText(text);
      setMagic(data.structured);
    } catch (err) {
      setError(`Magic fehlgeschlagen: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  // Sprint 43 · Magic mit lokalen Files via multipart (vor Lead-Save)
  async function executeMagicMultipart(): Promise<void> {
    setMagicAsk('closed');
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('raw_text', text);
      fd.append('target_field', field.id);
      const localImages = (localAttachments ?? [])
        .filter((a) => a.isImage)
        .slice(0, 4);
      for (const att of localImages) {
        fd.append('images', att.file);
      }
      const resp = await fetch('/api/text/magic-multipart', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
        body: fd,
      });
      if (!resp.ok) {
        const detail = await resp.json().catch(() => ({}));
        throw new Error(
          (detail as { detail?: string }).detail ?? `HTTP ${resp.status}`,
        );
      }
      const data = (await resp.json()) as { structured: MagicResult; vision_used?: boolean };
      setMagicRawText(text);
      setMagic(data.structured);
    } catch (err) {
      setError(`Magic fehlgeschlagen: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  const showLocalList = (localAttachments ?? []).length > 0;
  const canAddLocal = typeof onAddLocalAttachment === 'function';

  return (
    <div className="form-field">
      <label className="form-field__label" htmlFor={inputId}>
        {field.label.toUpperCase()}
        {field.required ? ' *' : ''}
      </label>
      <textarea
        id={inputId}
        className="auth-input form-field__textarea"
        required={field.required}
        rows={field.rows ?? 4}
        value={text}
        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
      />

      {/* Sprint 19 (S19.2) · Anhänge direkt unter Notiz-Textarea */}
      {(canAddLocal || showLocalList || showAttachmentControls) && (
        <div className="vm-attachments">
          {showLocalList && (
            <ul className="vm-attachments-list">
              {(localAttachments ?? []).map((att, idx) => (
                <li key={idx} className="vm-attachments-item">
                  {att.preview ? (
                    <img
                      src={att.preview}
                      alt={att.file.name}
                      className="vm-attachments-thumb"
                    />
                  ) : (
                    <div className="vm-attachments-thumb vm-attachments-thumb--pdf">
                      <ImageIcon size={16} />
                    </div>
                  )}
                  <span className="vm-attachments-name">{att.file.name}</span>
                  {onRemoveLocalAttachment && (
                    <button
                      type="button"
                      className="vm-attachments-remove"
                      onClick={() => onRemoveLocalAttachment(idx)}
                      aria-label={`Anhang ${att.file.name} entfernen`}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {canAddLocal && (
            <>
              <input
                ref={attachInputRef}
                type="file"
                accept={ATTACH_MIMES}
                onChange={handleAttachmentInput}
                style={{ display: 'none' }}
              />
              <button
                type="button"
                className="vm-attach-btn"
                onClick={() => attachInputRef.current?.click()}
                aria-label="Anhang hinzufügen"
              >
                <FilePlus2 size={14} />
                <span>+ Anhang</span>
              </button>
            </>
          )}
        </div>
      )}

      <div className="vm-actions">
        {field.voice && (
          <button
            type="button"
            disabled={busy}
            onClick={recording ? stopRecording : startRecording}
            className={`vm-btn ${recording ? 'vm-btn--rec' : ''}`}
          >
            {recording ? <Square size={16} fill="currentColor" /> : <Mic size={16} />}
            <span>{recording ? 'STOPPEN' : 'VOICE'}</span>
          </button>
        )}
        {field.magic && (
          <button
            type="button"
            disabled={busy || recording || !text.trim()}
            onClick={runMagic}
            className="vm-btn"
          >
            <Sparkles size={16} />
            <span>{serverImageCount > 0 ? `MAGIC · ${serverImageCount} 📷` : 'MAGIC'}</span>
          </button>
        )}
      </div>
      {/* Sprint 18 (S18.2) · Mockup-konforme Idle-/Busy-Hints */}
      <div className="vm-hints">
        {!busy && !recording && field.voice && (
          <Hint variant="info" icon={Mic}>
            Diktiere die Notiz · wird transkribiert
          </Hint>
        )}
        {!busy && !recording && field.magic && text.trim() && hasAnyImages && (
          <Hint variant="magic">
            KI strukturiert deine Notiz · Anhänge optional mit-auswerten
          </Hint>
        )}
        {!busy && !recording && field.magic && text.trim() && !hasAnyImages && (
          <Hint variant="magic">KI strukturiert deine Notiz</Hint>
        )}
        {busy && (
          <Hint variant="magic" icon={Loader2}>
            gpt-4o-mini · läuft…
          </Hint>
        )}
        {recording && (
          <Hint variant="warning" icon={Mic}>
            Aufnahme läuft · Stoppen drücken zum Transkribieren
          </Hint>
        )}
      </div>
      {error && <p className="auth-error">{error}</p>}
      {magic ? (
        <MagicDiffModal
          rawText={magicRawText}
          result={magic}
          onAccept={(formatted) => {
            onChange(formatted);
            setMagic(null);
            onMagicAccepted?.();
          }}
          onReject={() => setMagic(null)}
        />
      ) : null}

      {/* Sprint 41 · Schönes Modal statt window.confirm. Zwei Modi:
          1. choose-vision: Server-Bilder vorhanden → User wählt VISION oder TEXT-ONLY
          2. local-only-text: lokale Bilder vorhanden → nur TEXT-ONLY möglich */}
      {magicAsk !== 'closed' ? (
        <div className="modal-overlay" onClick={() => setMagicAsk('closed')}>
          <div
            className="modal-card magic-ask-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="magic-ask-modal__head">
              <Sparkles size={18} className="magic-ask-modal__icon" aria-hidden />
              <h2 className="app-shell__card-title" style={{ margin: 0 }}>
                KI-Strukturierung
              </h2>
            </div>
            {magicAsk === 'choose-vision-server' ? (
              <>
                <p className="magic-ask-modal__text">
                  Du hast {serverImageCount} Bild
                  {serverImageCount === 1 ? '' : 'er'} an diesen Lead angehängt.
                  Soll die KI die Bilder mit-auswerten?
                </p>
                <div className="magic-ask-modal__hint">
                  Vision-Magic kostet zusätzliche Token (~3-5× mehr) liefert aber
                  präzisere Strukturierung wenn die Notizen Bezüge zu den Bildern
                  haben (z.B. Skizzen, Whiteboard-Foto).
                </div>
                <div className="magic-ask-modal__actions">
                  <button
                    type="button"
                    className="auth-submit"
                    onClick={() =>
                      void executeMagicJson(
                        serverImageAtts.slice(0, 4).map((a) => a.id),
                      )
                    }
                  >
                    <Sparkles size={14} aria-hidden />
                    MIT BILDERN (Vision)
                  </button>
                  <button
                    type="button"
                    className="app-shell__logout"
                    onClick={() => void executeMagicJson([])}
                  >
                    NUR TEXT
                  </button>
                  <button
                    type="button"
                    className="app-shell__logout"
                    onClick={() => setMagicAsk('closed')}
                  >
                    ABBRECHEN
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="magic-ask-modal__text">
                  Du hast {localImageCount} lokales Bild
                  {localImageCount === 1 ? '' : 'er'} ausgewählt.
                  Soll die KI die Bilder mit-auswerten?
                </p>
                <div className="magic-ask-modal__hint">
                  <strong>Sprint 43:</strong> Vision-Magic funktioniert jetzt auch
                  vor dem Lead-Save — die Bilder werden direkt an OpenAI
                  geschickt ohne Persistierung. Kostet zusätzliche Tokens (~3-5×
                  mehr) liefert aber präzisere Strukturierung.
                </div>
                <div className="magic-ask-modal__actions">
                  <button
                    type="button"
                    className="auth-submit"
                    onClick={() => void executeMagicMultipart()}
                  >
                    <Sparkles size={14} aria-hidden />
                    MIT BILDERN (Vision)
                  </button>
                  <button
                    type="button"
                    className="app-shell__logout"
                    onClick={() => void executeMagicJson([])}
                  >
                    NUR TEXT
                  </button>
                  <button
                    type="button"
                    className="app-shell__logout"
                    onClick={() => setMagicAsk('closed')}
                  >
                    ABBRECHEN
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// Sprint 50 · React.memo · iPad-Tipp-Lag-Fix · rerendert nur wenn props
// shallow-equal unterscheiden. Voraussetzung: parent stabilisiert callbacks
// via useCallback und arrays via useMemo (siehe Capture.tsx).
export const VoiceMagicTextarea = memo(VoiceMagicTextareaInner);
